//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion


import { SystemService } from '../../../../services/system'
import { Enums } from '../../../../models/common/enums'
import { Provider, ProviderScope, Map, MapEvent } from './provider'
import { OpMarker, OpCoord, OpPolygon, OpRange, OpBounds, OpTooltip, OpPolyline, Polygon, Polyline, Range, Tooltip, Marker } from '../../../../models/custom/op-map-area'

declare var google: any
declare var MarkerClusterer: any
declare var OverlappingMarkerSpiderfier: any

export class GoogleProvider extends Provider {

    // Consts
    
    private static get PlusIconMarker(): string { return 'images/marker_plus.png' }
    private static get LOG_STRING(): string { return '[SIMAX.Common.Map.Provider.GoogleProvider] - %s'}

    // Consts End
    

    private map: any
    private mapPromise: JQueryPromise<any>

    private drawingManager: any

    private mapSpiderifier: any
    private mapCluster: any

    private openedTooltip: any

    private shouldZoom: boolean

    private markers: { [id: number]: any } = {}
    private selectedMarkers: OpMarker[] = []

    private polygons: { [id: number]: any } = {}
    private ranges: { [id: number]: any } = {}

    private selectedElements: any[] = []

    constructor(private $scope: ProviderScope, element: HTMLElement, onMapReady: (provider: Provider) => void, systemService: SystemService) {
        super($scope, systemService)

        let center = new OpCoord()
        center.latitude = 0
        center.longitude = 0
        if ($scope.options.initCenter) {
            center = $scope.options.initCenter
        }
        let zoom = 5
        if ($scope.options.initZoom >= 0) {
            zoom = $scope.options.initZoom
        }

        const mapOptions: any = {
            center: { lat: center.latitude, lng: center.longitude },
            zoom: zoom,
            fullscreenControl: false,
        }
        if ($scope.options.disableMapTypeControl)
            mapOptions.mapTypeControl = false
        if ($scope.options.disableStreetViewControl)
            mapOptions.streetViewControl = false

        var map = new google.maps.Map(element, mapOptions)

        var mapDeferred = $.Deferred<any>()
        var firstTime = true
        google.maps.event.addListenerOnce(map, 'idle', () => {
            this.map = map
            
            onMapReady(this)

            mapDeferred.resolve(map)

            var isCluster = false
            map.addListener('zoom_changed', _.debounce(() => {
                if (this.mapSpiderifier) {
                    var zoom = map.getZoom()
                    if ((firstTime || isCluster) && zoom > this.$scope.options.clusterZoom) {
                        isCluster = false
                        // reset spiderifier
                        if (this.mapSpiderifier) this.mapSpiderifier.removeAllMarkers()
                        var markersAtTheSamePosition: { [pos: string]: any } = {}
                        var markersAtTheSamePositionNotAdded: { [pos: string]: any } = {}
                        for (var id in this.markers) {
                            var marker = this.markers[id]
                            var stringPosition = marker.getPosition().lat().toString() + marker.getPosition().lng().toString()
                            if (markersAtTheSamePosition[stringPosition]) {
                                this.mapSpiderifier.addMarker(marker)
                                markersAtTheSamePositionNotAdded[stringPosition] = markersAtTheSamePosition[stringPosition]
                            } else
                                markersAtTheSamePosition[stringPosition] = marker
                        }
                        for (var pos in markersAtTheSamePositionNotAdded) {
                            this.mapSpiderifier.addMarker(markersAtTheSamePositionNotAdded[pos])
                        }
                        // ---
                    } else if ((firstTime || !isCluster) && zoom <= this.$scope.options.clusterZoom) {
                        isCluster = true
                    }
                    firstTime = false
                }
            }, 200))

            if ($scope.options.onMapClick) {
                map.addListener('click', (e) => {
                    let coord = new OpCoord()
                    coord.latitude = e.latLng.lat()
                    coord.longitude = e.latLng.lng()
                    $scope.options.onMapClick(coord)
                })
            }

            if (this.$scope.options.enableAreaSelect)
                this.initDrawingManager()
        })
        this.mapPromise = mapDeferred.promise()
    }

    public getBounds(): OpBounds {
        if (this.map) {
            let bounds = this.map.getBounds()
            let opBounds: OpBounds = this.googleMapBoundsToOpBounds(bounds)

            return opBounds
        }
        return null
    }

    public setBounds(bounds: OpBounds): void {
        if (this.map) {
            let b = new google.maps.LatLngBounds()
            b.extend(new google.maps.LatLng(bounds.southWest.latitude, bounds.southWest.longitude))
            b.extend(new google.maps.LatLng(bounds.northEast.latitude, bounds.northEast.longitude))
            this.map.fitBounds(b)
        }
    }

    public fitBounds(positions: OpCoord[]): void {
        if (this.map) {
            let bounds = new google.maps.LatLngBounds()
            positions.forEach(position => {
                bounds.extend(new google.maps.LatLng(position.latitude, position.longitude))
            })
            this.map.fitBounds(bounds)
        }
    }


    // Center Methods

    public setCenter(center: OpCoord): void {
        if (this.map) {
            var latLng = new google.maps.LatLng(center.latitude, center.longitude);
            this.map.setCenter(latLng)
        }
    }

    public center(): void {
        const bounds: any = this.getMarkersBounds()

        if (bounds != null) {
            setTimeout(() => {
                if (this.map != null) {
                    this.map.setCenter(bounds.getCenter())
                }
            }, 200)
        }
    }

    public centerIn(): void {
        const bounds: any = this.getSelectedMarkersBounds()

        if (bounds != null) {
            setTimeout(() => {
                if (this.map != null) {
                    this.map.setCenter(bounds.getCenter())
                }
            }, 200)
        }
    }

    public centerOnMarkers(markers: OpMarker[], animate: boolean): void {
        this.selectedMarkers = markers

        if (!markers || markers.length === 0) {
            this.clearSelectedElements()
            this.center()
        }
        else {
            this.centerIn()

            setTimeout(() => {
                this.drawSelectedElements(animate)
            }, 500)
        }
    }

    // Center Methods End


    // Zoom Methods

    public getZoom(): any {
        if (this.map)
            return this.map.getZoom()
        return null
    }

    public setZoom(zoom): any {
        if (this.map)
            return this.map.setZoom(zoom)
        return null
    }

    public zoomCenter(): void {
        var bounds = new google.maps.LatLngBounds()
        for (var id in this.markers) {
            bounds.extend(this.markers[id].getPosition())
        }

        setTimeout(() => {
            this.setFitPane(bounds)
        }, 200)
    }

    public zoomIn(): void {
        var bounds = new google.maps.LatLngBounds()
        if (this.selectedMarkers) {
            this.selectedMarkers.forEach(selectedMarker => {
                if (this.checkLatitude(selectedMarker.coords.latitude) && this.checkLongitude(selectedMarker.coords.longitude)) {
                    bounds.extend(new google.maps.LatLng(selectedMarker.coords.latitude, selectedMarker.coords.longitude))
                }
            })
            setTimeout(() => {
                this.setFitPane(bounds)
            }, 200)
        }
    }

    public zoomOnMarkers(markers: OpMarker[], animate: boolean): void {
        this.selectedMarkers = markers

        if (!markers || markers.length === 0) {
            this.clearSelectedElements()
            this.zoomCenter()
        }
        else {
            this.zoomIn()

            setTimeout(() => {
                this.drawSelectedElements(animate)
            }, 500)
        }
    }

    // Zoom Methods End


    public clearMarkers(): void {
        for (var id in this.markers) {
            this.markers[id].setMap(null)
        }
        this.markers = {}
    }

    public addMarker(marker: OpMarker): OpMarker {
        let mapMarker = this.createMarker(marker)
        return marker
    }

    public setMarkers(markers: OpMarker[]): void {
        if (this.openedTooltip) {
            this.openedTooltip.close()
            this.openedTooltip = null
        }
        this.clearSelectedElements()
        this.resetMapCluster()
        this.clearMarkers()
        if (markers) {
            this.mapPromise.then(map => {
                this.resetMapSpiderifier()

                var markersAtTheSamePosition: { [pos: string]: any } = {}
                var markersAtTheSamePositionNotAdded: { [pos: string]: any } = {}

                var mapMarkers = markers.map(marker => {
                    let newMarker = this.createMarker(marker)
                    if (newMarker) {
                        let mapMarker = newMarker.getMapMarker()
                        this.markers[marker.referenceValue] = mapMarker

                        // calculate spiderfier markers
                        var stringPosition = marker.coords.latitude.toString() + marker.coords.longitude.toString()
                        if (markersAtTheSamePosition[stringPosition]) {
                            this.mapSpiderifier.addMarker(mapMarker)
                            markersAtTheSamePositionNotAdded[stringPosition] = markersAtTheSamePosition[stringPosition]
                        } else
                            markersAtTheSamePosition[stringPosition] = mapMarker

                        return mapMarker
                    }
                    return null
                }).filter(m => m != null)

                // end calculate spiderfier markers
                for (var pos in markersAtTheSamePositionNotAdded) {
                    this.mapSpiderifier.addMarker(markersAtTheSamePositionNotAdded[pos])
                }

                // init cluster
                if (this.$scope.options.isClustering) {
                    this.mapCluster = new MarkerClusterer(map, mapMarkers, this.getMapClusterOptions())
                }
            })
        }
    }

    public clearPolygons(): void {
        for (var id in this.polygons) {
            this.polygons[id].setMap(null)
        }
        this.polygons = {}
    }

    public addPolygon(polygon: OpPolygon): OpPolygon {
        let coords: { lat: number, lng: number }[] = polygon.coords.map(c => {
            return {
                lat: c.latitude,
                lng: c.longitude
            }
        })

        let mapPolygon: any = new google.maps.Polygon({
            paths: coords,
            strokeColor: polygon.lineColor,
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: polygon.fillColor,
            fillOpacity: 0.35,
            map: this.map
        })

        let polygonDecorator: Polygon = {
            isShown: () => {
                return mapPolygon.getMap() != null
            },
            show: () => {
                mapPolygon.setMap(this.map)
            },
            hide: () => {
                mapPolygon.setMap(null)
            },
            getMapPolygon: () => {
                return mapPolygon
            }
        }

        polygon.setPolygon(polygonDecorator)

        return polygon
    }

    public setPolygons(polygons: OpPolygon[]): void {
        this.clearPolygons()
        if (polygons) {
            this.mapPromise.then(map => {
                polygons.forEach(polygon => {
                    let newPolygon: OpPolygon = this.addPolygon(polygon)
                    let mapPolygon: any = newPolygon.getPolygon().getMapPolygon()

                    if (polygon.tooltip) {
                        let infoTooltip: any = new google.maps.InfoWindow({
                            content: polygon.tooltip,
                        })

                        mapPolygon.addListener('click', (e) => {
                            if (!this.$scope.options.hideTooltip) {
                                if (this.openedTooltip)
                                    this.openedTooltip.close()
                                infoTooltip.setPosition(e.latLng)
                                infoTooltip.open(map)
                                this.openedTooltip = infoTooltip
                            }
                        })
                    }

                    this.polygons[polygon.referenceValue] = mapPolygon
                })
            })
        }
    }

    public clearRanges(): void {
        for (var id in this.ranges) {
            this.ranges[id].setMap(null)
        }
        this.ranges = {}
    }

    public addRange(range: OpRange): OpRange {
        let mapCircle: any = new google.maps.Circle({
            center: {
                lat: range.coords.latitude,
                lng: range.coords.longitude
            },
            strokeColor: range.lineColor,
            strokeOpacity: range.lineOpacity,
            strokeWeight: range.lineWeight,
            fillColor: range.fillColor,
            fillOpacity: range.fillOpacity,
            radius: range.radius,
            /* original options for selection */
            originalOptions: {
                strokeColor: range.lineColor,
                strokeWeight: range.lineWeight,
                fillOpacity: range.fillOpacity
            },
            map: this.map
        })

        let rangeDecorator: Range = {
            isShown: () => {
                return mapCircle.getMap() != null
            },
            show: () => {
                mapCircle.setMap(this.map)
            },
            hide: () => {
                mapCircle.setMap(null)
            },
            getMapRange: () => {
                return mapCircle
            }
        }

        range.setRange(rangeDecorator)

        return range
    }

    public setRanges(ranges: OpRange[]): void {
        this.clearRanges()
        if (ranges) {
            this.mapPromise.then(map => {
                ranges.forEach(range => {
                    let addedRange: OpRange = this.addRange(range)
                    let mapRange: any = addedRange.getRange().getMapRange()

                    if (range.selectedLineColor && range.selectedLineWeight && range.selectedFillOpacity) {
                        mapRange.selectedOptions = {
                            strokeColor: range.selectedLineColor,
                            strokeWeight: range.selectedLineWeight,
                            fillOpacity: range.selectedFillOpacity
                        }
                    }

                    if (range.tooltip) {
                        let infoTooltip = new google.maps.InfoWindow({
                            content: range.tooltip,
                            position: mapRange.center
                        })

                        mapRange.addListener('click', (e) => {
                            if (!this.$scope.options.hideTooltip) {
                                if (this.openedTooltip)
                                    this.openedTooltip.close()
                                infoTooltip.setPosition(e.latLng)
                                infoTooltip.open(map)
                                this.openedTooltip = infoTooltip
                            }
                        })
                    }

                    this.ranges[range.referenceValue] = mapRange
                })
            })
        }
    }

    public enableAreaSelect(callback: (bounds) => void): void {
        this.drawingManager.setDrawingMode(google.maps.drawing.OverlayType.RECTANGLE)
        google.maps.event.addListenerOnce(this.drawingManager, 'rectanglecomplete', (rectangle) => {
            rectangle.setMap(null)
            this.drawingManager.setDrawingMode(null)

            let opBounds: OpBounds = this.googleMapBoundsToOpBounds(rectangle.getBounds())

            callback(opBounds)
        })
    }

    public disableAreaSelect(): void {
        this.drawingManager.setDrawingMode(null)
    }

    public clearListeners(event: MapEvent, marker: OpMarker): void {
        google.maps.event.clearListeners(marker.getMapMarker(), MapEvent[event].toLowerCase())
    }

    public computeDistanceBetweenCoords(c1: OpCoord, c2: OpCoord): number {
        var p1 = new google.maps.LatLng(c1.latitude, c1.longitude)
        var p2 = new google.maps.LatLng(c2.latitude, c2.longitude)

        return google.maps.geometry.spherical.computeDistanceBetween(p1, p2)
    }

    public createMarkerTooltip(marker: OpMarker, text: string): OpTooltip {
        let infoWindow = new google.maps.InfoWindow({
            content: text
        })

        infoWindow.isVisible = false

        let tooltipDecorator: Tooltip = {
            show: () => {
                if (marker.isSetOnMap()) {
                    infoWindow.open(this.map, marker.getMapMarker())
                    infoWindow.isVisible = true
                }
            },
            hide: () => {
                if (infoWindow.isVisible)
                    infoWindow.close()
                infoWindow.isVisible = false
            }
        }

        let opTooltip = new OpTooltip()
        opTooltip.setTooltip(tooltipDecorator)

        return opTooltip
    }

    public resize(): void {
        // Nie używane w GoogleMap provider, metoda dziedziczona z klasy abstrakcyjnej 
    }

    public addPolyline(polyline: OpPolyline): OpPolyline {
        let mapPolyline: any = new google.maps.Polyline({
            path: polyline.positions.map(position => {
                return {
                    lat: position.latitude,
                    lng: position.longitude
                }
            }),
            geodesic: true,
            strokeColor: polyline.lineColor,
            strokeOpacity: polyline.lineOpacity,
            strokeWeight: polyline.lineWeight
        })

        let polylineDecorator: Polyline = {
            isShown: () => {
                return mapPolyline.getMap() != null
            },
            show: () => {
                mapPolyline.setMap(this.map)
            },
            hide: () => {
                mapPolyline.setMap(null)
            },
            getMapPolyline: () => {
                return mapPolyline
            }
        }

        polyline.setPolyline(polylineDecorator)

        return polyline
    }

    private createMarker(marker: OpMarker): OpMarker {
        if (!this.checkLatitude(marker.coords.latitude) || !this.checkLongitude(marker.coords.longitude)) {
            return null
        }

        const icon: any = this.getMarkerIcon(marker, this.$scope.options)

        if (icon == null) {
            console.warn(GoogleProvider.LOG_STRING, "No default marker icon options. Using the default google maps one.")
        }

        const mapMarker: any = new google.maps.Marker({
            position: new google.maps.LatLng(marker.coords.latitude, marker.coords.longitude),
            icon: icon,
            originalIcon: icon,
            map: this.map
        })

        var infoTooltip: any = null
        if (marker.tooltip)
            infoTooltip = new google.maps.InfoWindow({
                content: marker.tooltip,
                pixelOffset: new google.maps.Size(0, -15)
            })

        var markerClickTimeout: any = null
        mapMarker.addListener('dblclick', e => {
            if (markerClickTimeout) {
                clearTimeout(markerClickTimeout)
                markerClickTimeout = null
            }

            if (marker.onDoubleClick) {
                marker.onDoubleClick(marker)
            }
        })

        mapMarker.addListener('click', (e) => {
            markerClickTimeout = setTimeout(() => {
                if (infoTooltip && !this.$scope.options.hideTooltip) {
                    if (this.openedTooltip && !this.$scope.options.allowMultiTooltipVisibility)
                        this.openedTooltip.close()
                    if (!mapMarker.hideTooltip) {
                        infoTooltip.setPosition(mapMarker.position)
                        infoTooltip.open(this.map)
                        this.openedTooltip = infoTooltip
                    } else
                        mapMarker.hideTooltip = false
                }
                if (marker.onClick) {
                    marker.onClick(marker)
                }

                this.selectedMarkers = [marker]
                // 2018-10-04 Ten zabieg nie ma sensu, po co dokładamy sztucznie do zaznacoznych
                // markerów related markery?
                //if (marker.relatedMarkers && marker.relatedMarkers.length > 0) {
                //    this.selectedMarkers.push(...marker.relatedMarkers)
                //}
                this.$scope.showZoomInButton = true

                this.drawSelectedElements(true) //TO DO!!!

                markerClickTimeout = null
            }, 300)
        })

        let markerDecorator: Marker = {
            isShown: () => {
                return mapMarker.getMap() != null
            },
            show: () => {
                mapMarker.setMap(this.map)
            },
            hide: () => {
                mapMarker.setMap(null)
            },
            setZIndex: (index: number) => {
                mapMarker.setZIndex(index)
            },
            addListener: (event: Enums.SimaxMarkerEvent, listener: (event) => void) => {
                mapMarker.addListener(Enums.SimaxMarkerEvent[event].toLowerCase(), listener)
            },
            getPosition: () => {
                let position = mapMarker.getPosition()
                let coord = new OpCoord()
                coord.latitude = position.lat()
                coord.longitude = position.lng()
                return coord
            },
            setIcon: (icon: string, scaledSize: { width: number, height: number } = null) => {
                let i: any = {
                    url: icon
                }
                if (scaledSize) {
                    i.scaledSize = new google.maps.Size(scaledSize.width, scaledSize.height)
                }
                mapMarker.setIcon(i)
            },
            getMapMarker: () => {
                return mapMarker
            },
            setPosition: (position: OpCoord) => {
                mapMarker.setPosition(new google.maps.LatLng(position.latitude, position.longitude))
            }
        }

        marker.setMarker(markerDecorator)

        return marker
    }

    private getMarkersBounds(): any {
        if (this.markers != null && _.keys(this.markers).length > 0) {
            const bounds: any = new google.maps.LatLngBounds()

            for (let id in this.markers) {
                bounds.extend(this.markers[id].getPosition())
            }

            return bounds
        }

        return null
    }

    private getSelectedMarkersBounds(): any {
        if (this.selectedMarkers != null && this.selectedMarkers.length > 0) {
            const bounds: any = new google.maps.LatLngBounds()

            this.selectedMarkers.forEach((selectedMarker: OpMarker) => {
                if (this.checkLatitude(selectedMarker.coords.latitude) && this.checkLongitude(selectedMarker.coords.longitude)) {
                    bounds.extend(new google.maps.LatLng(selectedMarker.coords.latitude, selectedMarker.coords.longitude))
                }
            })

            return bounds
        }

        return null
    }

    private setFitPane(markerBounds): void {
        if (this.map) {
            this.map.fitBounds(markerBounds)
            this.map.panToBounds(markerBounds)
            if (this.map.getZoom() > this.$scope.options.maxZoom)
                this.map.setZoom(this.$scope.options.maxZoom)
        }
    }

    private resetMapSpiderifier(): void {
        if (this.map) {
            if (this.mapSpiderifier) this.mapSpiderifier.removeAllMarkers()
            this.mapSpiderifier = new OverlappingMarkerSpiderfier(this.map, {
                nearbyDistance: 1,
                keepSpiderfied: true,
                legWeight: 1
            })
            var drawAfterSpiderfiable = _.debounce(() => {
                this.drawSelectedElements(false)
            }, 500)
            this.mapSpiderifier.addListener('format', (marker, status) => {
                if (status == OverlappingMarkerSpiderfier.markerStatus.SPIDERFIABLE) {
                    marker.setIcon(GoogleProvider.PlusIconMarker)
                    marker.isSpiderfied = true
                    drawAfterSpiderfiable()
                } else if (status == OverlappingMarkerSpiderfier.markerStatus.SPIDERFIED || status == OverlappingMarkerSpiderfier.markerStatus.UNSPIDERFIABLE) {
                    marker.setIcon(marker.originalIcon)
                    marker.isSpiderfied = false
                }
            })
            this.mapSpiderifier.addListener('unspiderfy', () => {
                if (this.openedTooltip) {
                    this.openedTooltip.close()
                }
            })
        }
    }

    private getMapClusterOptions(): { zoomOnClick: boolean, gridSize: number, maxZoom: any, imagePath: string } {
        return {
            zoomOnClick: true,
            gridSize: 30,
            maxZoom: this.$scope.options.clusterZoom,
            imagePath: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m'
        }
    }

    private resetMapCluster(): void {
        if (this.mapCluster) {
            this.mapCluster.clearMarkers()
            this.mapCluster.resetViewport()
        }
    }

    private clearSelectedElements(): void {
        this.selectedElements.forEach(element => {
            if (element.clear) {
                element.clear()
                delete element.clear
            }
        })
        this.selectedElements = []
    }

    private drawSelectedElements(animate: boolean): void {
        // restore
        this.clearSelectedElements()

        // draw, change, etc.
        if (this.selectedMarkers && this.selectedMarkers.length > 0 && this.markers) {
            this.selectedMarkers.forEach(selectedMarker => {
                var marker: any = this.markers[selectedMarker.referenceValue]
                if (marker) {
                    if (marker.isSpiderfied) {
                        marker.setIcon(GoogleProvider.PlusIconMarker)
                    } else if (animate) {
                        this.animeMarker(marker)
                    }
                    marker.clear = () => {
                        if (marker.isSpiderfied) {
                            marker.setIcon(GoogleProvider.PlusIconMarker)
                        }
                    }
                    this.selectedElements.push(marker)

                    var range: any = this.ranges[selectedMarker.referenceValue]
                    if (range && range.selectedOptions) {
                        range.setOptions({ strokeWeight: range.selectedOptions.strokeWeight, strokeColor: range.selectedOptions.strokeColor, fillOpacity: range.selectedOptions.fillOpacity })
                        range.clear = () => {
                            range.setOptions({ strokeWeight: range.originalOptions.strokeWeight, strokeColor: range.originalOptions.strokeColor, fillOpacity: range.originalOptions.fillOpacity })
                        }
                        this.selectedElements.push(range)
                    }

                    // TODO: here add polygons and ranges

                    var parentPosition = marker._omsData && marker._omsData.v ? marker._omsData.v : marker.position

                    if (selectedMarker.relatedMarkers && selectedMarker.relatedMarkers.length > 0) {
                        selectedMarker.relatedMarkers.forEach(relatedMarker => {
                            var rMarker = this.markers[relatedMarker.referenceValue]
                            if (rMarker) {
                                this.animeMarker(rMarker)

                                var childPosition = rMarker._omsData && rMarker._omsData.v ? rMarker._omsData.v : rMarker.position
                                var path = new google.maps.Polyline({
                                    path: [parentPosition, childPosition],
                                    geodesic: true,
                                    strokeColor: '#aaa',
                                    strokeOpacity: 0.5,
                                    strokeWeight: 1,
                                    map: this.map,
                                    clear: () => {
                                        path.setMap(null)
                                    }
                                })
                                this.selectedElements.push(path)
                            }
                        })
                    }
                }
            })
        }
    }

    private animeMarker(marker): void {
        if (!marker.isSpiderfied) {
            marker.setAnimation(google.maps.Animation.BOUNCE)
            setTimeout(() => {
                marker.setAnimation(null)
            }, 3000)
        }
    }

    private initDrawingManager(): void {
        if (!this.drawingManager) {
            var drawingManager: any = new google.maps.drawing.DrawingManager({
                drawingControl: false,
                drawingMode: null,
                rectangleOptions: {
                    strokeWeight: 1
                }
            })
            drawingManager.setMap(this.map)
            this.drawingManager = drawingManager
        } else {
            this.drawingManager.setMap(this.map)
        }
    }

    private googleMapBoundsToOpBounds(bounds: any): OpBounds {

        let opBounds: OpBounds = null;

        if (bounds
            && bounds.getSouthWest
            && bounds.getNorthEast)
        {
            try {
                let sw = bounds.getSouthWest()
                let ne = bounds.getNorthEast()

                let swCoord: OpCoord = new OpCoord()
                swCoord.latitude = sw.lat()
                swCoord.longitude = sw.lng()

                let neCoord: OpCoord = new OpCoord()
                neCoord.latitude = ne.lat()
                neCoord.longitude = ne.lng()

                opBounds = new OpBounds()
                opBounds.northEast = neCoord
                opBounds.southWest = swCoord
            } catch (e) {
                console.log("Failed to convert google maps bounds to OpBounds: " + e)
            }
        }

        return opBounds
    }
}